import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Button,
} from '@mui/material';
import { useChecklist } from '../contexts/ChecklistContext';
import { useAuth } from '../contexts/AuthContext';
import CircularProgressChart from './CircularProgressChart';
import Sidebar, { drawerWidth, collapsedDrawerWidth } from '../components/Sidebar';
import {
  Security as SecurityIcon,
  Lock as LockIcon,
  Web as WebIcon,
  Email as EmailIcon,
  Message as MessageIcon,
  Group as GroupIcon,
  Router as RouterIcon,
  PhoneAndroid as PhoneIcon,
  Computer as ComputerIcon,
  Home as HomeIcon,
  AccountBalance as FinanceIcon,
  Person as PersonIcon,
  Shield as ShieldIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

const ChecklistDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getProgressStats, getCategoryProgress } = useChecklist();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  const stats = getProgressStats();
  const categoryProgress = getCategoryProgress();

  if (!user) {
    return null;
  }

  const handleModuleClick = () => {
    navigate('/checklist/basissicherheit');
  };

  // Nur Basissicherheit Lernpfad anzeigen
  const basissicherheitCategory = categoryProgress.find(cat => cat.name === 'Basissicherheit');
  
  const getModuleIcon = (moduleName) => {
    const iconMap = {
      'Passwort-Manager': <LockIcon />,
      'MFA': <SecurityIcon />,
      'Sichere Passwörter': <LockIcon />,
      'Ad-Blocker': <WebIcon />,
      'Email Account absichern': <EmailIcon />,
      'Social Media': <GroupIcon />,
      'Router Passwort ändern': <RouterIcon />,
      'App-Berechtigungen': <PhoneIcon />,
      'Automatische Updates': <PhoneIcon />,
      'Backups': <ComputerIcon />,
    };
    return iconMap[moduleName] || <SecurityIcon />;
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)',
      minHeight: '100vh', 
      color: '#ffffff',
      position: 'relative',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(66, 165, 245, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0,
      }
    }}>
      <Sidebar 
        open={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        mobileOpen={mobileSidebarOpen}
        onMobileToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
      />
      
      <Box sx={{ 
        display: 'flex',
        ml: { sm: sidebarOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` },
        transition: (theme) => theme.transitions.create('margin-left', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        pt: { xs: 2, sm: 3, md: 4 },
        pb: 4,
      }}>
        <Container maxWidth="lg" sx={{ flexGrow: 1, width: '100%' }}>
        {/* Zurück zum Lagebild Link */}
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{
              color: '#42a5f5',
              '&:hover': {
                backgroundColor: 'rgba(66, 165, 245, 0.1)',
              },
            }}
          >
            Zurück zum Lagebild
          </Button>
        </Box>
        
        {/* Progress Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 6, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            borderRadius: 3
          }}
        >
          {stats.completed === 0 ? (
            <>
              <Typography variant="h6" sx={{ mb: 2, color: '#b0b0b0' }}>
                Noch keine Statistiken
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#b0b0b0' }}>
                Du siehst hier deinen Fortschritt, sobald du beginnst, die einzelnen Module abzuhaken
              </Typography>
            </>
          ) : (
            <Typography variant="h6" sx={{ mb: 2, color: '#4caf50' }}>
              🎉 Großartiger Fortschritt!
            </Typography>
          )}
          <Typography variant="h6" sx={{ mb: 1, color: '#ffffff' }}>
            Dein Fortschritt
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: '#ffffff' }}>
            Du hast <strong>{stats.completed} von {stats.total}</strong> Punkten abgeschlossen
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, color: '#42a5f5' }}>
            {stats.percentage}% abgeschlossen
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#b0b0b0' }}>
            {stats.completed === 0 
              ? 'Beginne, indem du unten eine Checkliste auswählen'
              : 'Weiter so! Wähle eine weitere Kategorie aus, um deinen Fortschritt zu steigern'
            }
          </Typography>
        </Paper>

        {/* Progress Charts Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 6, 
            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            borderRadius: 3
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4, 
              textAlign: 'center',
              fontWeight: 600,
              color: '#ffffff'
            }}
          >
            Dein Fortschritt
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            {/* Overall Progress */}
            <Grid item xs={12} sm={6} md={3}>
              <CircularProgressChart
                size={140}
                strokeWidth={10}
                percentage={stats.percentage}
                color="#4caf50"
                backgroundColor="#e8f5e8"
                label="Gesamtfortschritt"
                subtitle={`${stats.completed} von ${stats.total} Punkten`}
              />
            </Grid>
            
            {/* Modules Completed */}
            <Grid item xs={12} sm={6} md={3}>
              <CircularProgressChart
                size={140}
                strokeWidth={10}
                percentage={basissicherheitCategory ? basissicherheitCategory.percentage : 0}
                color="#2196f3"
                backgroundColor="#e3f2fd"
                label="Module abgeschlossen"
                subtitle={basissicherheitCategory ? `${basissicherheitCategory.completed} von ${basissicherheitCategory.total}` : '0 von 10'}
              />
            </Grid>
            
            {/* Security Level */}
            <Grid item xs={12} sm={6} md={3}>
              <CircularProgressChart
                size={140}
                strokeWidth={10}
                percentage={Math.min(100, Math.round((stats.percentage / 100) * 100))}
                color="#9c27b0"
                backgroundColor="#f3e5f5"
                label="Sicherheitslevel"
                subtitle={stats.percentage === 100 ? "Basissicherheit erreicht" : stats.percentage >= 80 ? "Fast geschafft" : stats.percentage >= 60 ? "Schon gut dabei" : stats.percentage >= 40 ? "Macht Fortschritte" : stats.percentage >= 20 ? "Erste Schritte geschafft" : "Bereit zu starten"}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Basissicherheit Lernpfad */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 2, 
              textAlign: 'center',
              fontWeight: 600,
              color: '#ffffff'
            }}
          >
            Basissicherheit Lernpfad
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4, 
              textAlign: 'center',
              color: '#b0b0b0'
            }}
          >
            Arbeite die 10 Basissicherheit-Module ab, um deine digitale Sicherheit zu verbessern
          </Typography>
        </Box>

        {basissicherheitCategory && (
          <Card 
            onClick={handleModuleClick}
            sx={{ 
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              mb: 4,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(33, 150, 243, 0.3)',
                border: '1px solid rgba(66, 165, 245, 0.3)',
              }
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  color: '#42a5f5', 
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '3rem'
                }}>
                  <SecurityIcon sx={{ fontSize: '3rem' }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#ffffff', mb: 1 }}>
                    Basissicherheit
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#b0b0b0' }}>
                    10 Module für grundlegende digitale Sicherheit
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#b0b0b0', mb: 1 }}>
                    {basissicherheitCategory.completed === 0 
                      ? 'Noch nicht begonnen' 
                      : basissicherheitCategory.completed === basissicherheitCategory.total 
                        ? 'Abgeschlossen' 
                        : 'In Bearbeitung'}
                  </Typography>
                  <Box
                    sx={{
                      width: '300px',
                      height: 8,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 4,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${basissicherheitCategory.percentage}%`,
                        height: '100%',
                        backgroundColor: basissicherheitCategory.completed === basissicherheitCategory.total ? '#4caf50' : '#2196f3',
                        transition: 'width 0.3s ease-in-out',
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h5" sx={{ color: '#42a5f5', fontWeight: 600 }}>
                    {basissicherheitCategory.completed}/{basissicherheitCategory.total} Module
                  </Typography>
                  {basissicherheitCategory.percentage > 0 && (
                    <Typography variant="body2" sx={{ color: '#4caf50', mt: 0.5 }}>
                      {basissicherheitCategory.percentage}% abgeschlossen
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Coming Soon Message */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(15, 15, 15, 0.5) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: 3
          }}
        >
          <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
            🔒 Weitere Lernpfade
          </Typography>
          <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
            Weitere Kategorien und Lernpfade werden in Kürze freigeschaltet. Konzentriere dich zunächst auf die Basissicherheit-Module.
          </Typography>
        </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default ChecklistDashboard;

